<template>
  <div>
    <!-- Banner -->
    <div
      v-if="showBanner"
      :class="['cc-banner', { 'cc-banner--moxi-dk': isMoxiDk, 'cc-banner--football': isFootball }]"
      role="dialog"
      aria-live="polite"
      aria-label="cookie consent"
    >
      <div class="cc-inner">
        <div class="cc-title">{{ $t('consent.title') }}</div>
        <div class="cc-text">
          {{ $t('consent.text') }}
        </div>

        <div class="cc-actions">
          <button class="cc-btn cc-btn-muted cc-btn-customize" type="button" @click="openPreferences">
            {{ $t('consent.customize') }}
          </button>

          <div class="cc-actions-right">
            <button class="cc-btn cc-btn-muted cc-btn-reject" type="button" @click="rejectAll">
              {{ $t('consent.reject') }}
            </button>
            <button class="cc-btn cc-btn-primary" type="button" @click="acceptAll">
              {{ $t('consent.accept') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Preferences modal -->
    <div v-if="showPreferences" :class="['cc-modal-backdrop', { 'cc-modal-backdrop--football': isFootball }]" role="dialog" aria-modal="true">
      <div class="cc-modal">
        <div class="cc-modal-header">
          <div class="cc-modal-title">{{ $t('consent.modal.title') }}</div>
          <button class="cc-icon-btn" type="button" @click="closePreferences" :aria-label="$t('consent.modal.close')">
            ✕
          </button>
        </div>

        <div class="cc-modal-body">
          <p class="cc-modal-desc">{{ $t('consent.modal.desc') }}</p>

          <div class="cc-pref">
            <div class="cc-pref-left">
              <div class="cc-pref-name">{{ $t('consent.modal.necessary.title') }}</div>
              <div class="cc-pref-desc">{{ $t('consent.modal.necessary.desc') }}</div>
            </div>
            <div class="cc-pref-right">
              <span class="cc-pill">{{ $t('consent.modal.alwaysOn') }}</span>
            </div>
          </div>

          <div class="cc-pref">
            <div class="cc-pref-left">
              <div class="cc-pref-name">{{ $t('consent.modal.analytics.title') }}</div>
              <div class="cc-pref-desc">{{ $t('consent.modal.analytics.desc') }}</div>
            </div>
            <div class="cc-pref-right">
              <label class="cc-switch">
                <input type="checkbox" v-model="prefs.analytics" />
                <span class="cc-slider"></span>
              </label>
            </div>
          </div>

          <div class="cc-pref">
            <div class="cc-pref-left">
              <div class="cc-pref-name">{{ $t('consent.modal.ads.title') }}</div>
              <div class="cc-pref-desc">{{ $t('consent.modal.ads.desc') }}</div>
            </div>
            <div class="cc-pref-right">
              <label class="cc-switch">
                <input type="checkbox" v-model="prefs.ads" />
                <span class="cc-slider"></span>
              </label>
            </div>
          </div>

          <div class="cc-links">
            <router-link class="cc-link" to="/cookies-policy" @click.native="closePreferences">
              {{ $t('consent.modal.policyLink') }}
            </router-link>
          </div>
        </div>

        <div class="cc-modal-footer">
          <button class="cc-btn cc-btn-muted cc-btn-reject" type="button" @click="rejectAll">
            {{ $t('consent.reject') }}
          </button>
          <button class="cc-btn cc-btn-primary" type="button" @click="savePreferences">
            {{ $t('consent.modal.save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { readStoredConsent, storeConsent, updateConsent } from '@/helper/consent';
import { helpers } from '@/helper/portal_config';

export default {
  name: 'CookieConsent',
  data() {
    return {
      showBanner: false,
      showPreferences: false,
      prefs: {
        analytics: false,
        ads: false,
      },
    };
  },
  mounted() {
    const stored = readStoredConsent();
    if (stored) {
      this.showBanner = false;
      this.prefs.analytics = !!stored.analytics;
      this.prefs.ads = !!stored.ads;
      // Consent is already applied during initConsentMode().
      return;
    }
    this.showBanner = true;
  },
  computed: {
    // DK + Moxi: match Moxi palette for the consent banner background
    isMoxiDk() {
      try {
        const country = (helpers?.active_country || '').toUpperCase();
        const landingName = (helpers?.landing_name_array || '').toString().toLowerCase();
        return country === 'DK' && landingName.includes('moxi');
      } catch (e) {
        return false;
      }
    },
    // Football landing: render banner + preferences modal in dark mode.
    isFootball() {
      try {
        const landingName = (helpers?.landing_name_array || '').toString().toLowerCase();
        return landingName.includes('football');
      } catch (e) {
        return false;
      }
    },
  },
  methods: {
    openPreferences() {
      this.showPreferences = true;
    },
    closePreferences() {
      this.showPreferences = false;
    },
    acceptAll() {
      const saved = storeConsent({ analytics: true, ads: true });
      updateConsent(saved);
      this.showBanner = false;
      this.showPreferences = false;
    },
    rejectAll() {
      const saved = storeConsent({ analytics: false, ads: false });
      updateConsent(saved);
      this.showBanner = false;
      this.showPreferences = false;
    },
    savePreferences() {
      const saved = storeConsent({ analytics: this.prefs.analytics, ads: this.prefs.ads });
      updateConsent(saved);
      this.showBanner = false;
      this.showPreferences = false;
    },
  },
};
</script>

<style>

/* Banner */
.cc-banner{
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  padding: 18px 14px 20px;
  background-color: #FFFFFF !important;
  color: #3C3C3C !important;
  font-family: -apple-system,sans-serif !important;
  font-size: 11px !important;
  border-top: 1px solid rgba(0,0,0,0.08);
}

/* DK + Moxi: light-blue background to match the Moxi palette */
.cc-banner.cc-banner--moxi-dk{
  background-color: #89CFF0 !important;
}
.cc-banner.cc-banner--moxi-dk .cc-title{
  background-color: transparent !important;
}
.cc-inner{
  max-width: 1100px;
  margin: 0 auto;
}
.cc-title{
  font-weight: 700 !important;
  font-size: 14px !important;
  background-color: #FFFFFF !important;
  color: #3C3C3C !important;
  margin-bottom: 8px;
}
.cc-text{
  font-size: 11px !important;
  line-height: 1.35;
  color: #3C3C3C !important;
  margin-bottom: 12px;
}
.cc-actions{
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}
.cc-actions-right{
  display: flex;
  gap: 10px;
  align-items: center;
}
.cc-btn{
  border: 0;
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 700;
  font-size: 11px !important;
  cursor: pointer;
  outline: none;
}
.cc-btn-muted{
  background-color: #D8D1D1 !important;
  color: #7F7B7B !important;
}
.cc-btn-primary{
background-color: #3C3C3C !important;
  color: #FFFFFF !important;
  -webkit-text-fill-color: #FFFFFF !important;
  text-transform: uppercase !important;
}
/* Modal */
.cc-modal-backdrop{
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 14px;
}
.cc-modal{
  width: 100%;
  max-width: 720px;
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(0,0,0,0.25);
  font-family: -apple-system,sans-serif !important;
  color: #3C3C3C !important;
  font-size: 11px !important;
}
.cc-modal-header{
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0,0,0,0.08);
}
.cc-modal-title{
  font-weight: 800;
  font-size: 14px !important;
}
.cc-icon-btn{
  border: 0;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
}
.cc-modal-body{
  padding: 14px 16px 4px;
}
.cc-modal-desc{
  margin: 0 0 12px;
  color: #333;
  font-size: 14px;
  line-height: 1.4;
}
.cc-pref{
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid rgba(0,0,0,0.06);
}
.cc-pref:first-of-type{
  border-top: 0;
}
.cc-pref-name{
  font-weight: 800;
  font-size: 14px;
  margin-bottom: 4px;
}
.cc-pref-desc{
  font-size: 13px;
  color: #444;
  line-height: 1.35;
}
.cc-pill{
  display: inline-block;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0,0,0,0.08);
  font-size: 12px;
  font-weight: 700;
  color: #333;
}
.cc-links{
  padding: 10px 0 6px;
}
.cc-link{
  color: #3C3C3C !important;
  font-weight: 700;
  text-decoration: underline;
  font-size: 11px !important;
}
.cc-modal-footer{
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 12px 16px 16px;
  border-top: 1px solid rgba(0,0,0,0.08);
}

/* Switch */
.cc-switch{
  position: relative;
  display: inline-block;
  width: 46px;
  height: 26px;
}
.cc-switch input{
  opacity: 0;
  width: 0;
  height: 0;
}
.cc-slider{
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.18);
  transition: .2s;
  border-radius: 999px;
}
.cc-slider:before{
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  top: 3px;
  background-color: white;
  transition: .2s;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0,0,0,0.18);
}
.cc-switch input:checked + .cc-slider{
  background-color: rgba(0,0,0,0.82);
}
.cc-switch input:checked + .cc-slider:before{
  transform: translateX(20px);
}

/* Mobile: stack actions */
@media (max-width: 540px){
  .cc-title{
  font-weight: 700 !important;
  font-size: 14px !important;
  background-color: #FFFFFF !important;
  color: #3C3C3C !important;
  margin-bottom: 8px;
}
.cc-actions{ flex-direction: column; align-items: stretch; }
  .cc-actions-right{ width: 100%; }
  .cc-actions-right .cc-btn{
  border: 0;
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 700;
  font-size: 11px !important;
  cursor: pointer;
  outline: none;
}
.cc-btn{
  border: 0;
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 700;
  font-size: 11px !important;
  cursor: pointer;
  outline: none;
}
.cc-modal-backdrop{ align-items: flex-end; }
  .cc-modal{
  width: 100%;
  max-width: 720px;
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(0,0,0,0.25);
  font-family: -apple-system,sans-serif !important;
  color: #3C3C3C !important;
  font-size: 11px !important;
}
.cc-modal-footer{ justify-content: space-between; }
  .cc-modal-footer .cc-btn{
  border: 0;
  border-radius: 999px;
  padding: 12px 18px;
  font-weight: 700;
  font-size: 11px !important;
  cursor: pointer;
  outline: none;
}
}

@media (max-width: 600px) {
  .cc-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  /* First line: Reject + I agree */
  .cc-actions-right {
    order: 1;
    width: 100%;
    display: flex;
    gap: 10px;
  }
  .cc-actions-right .cc-btn {
    flex: 1 1 0;
    width: 50%;
  }
  /* Second line: Customize full width */
  .cc-btn-customize {
    order: 2;
    width: 100%;
  }
}

/* ============================================================================
   FOOTBALL DESIGN — dark mode for cookie consent banner + preferences modal.
   Applied when landing_name_array contains "football" (Goalplaza / Nationfoot).
   ============================================================================ */
.cc-banner.cc-banner--football {
  background-color: #080d11 !important;
  color: #e6edf3 !important;
  border-top: 1px solid rgba(255, 255, 255, 0.10) !important;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.5);
}
.cc-banner--football .cc-title {
  background-color: transparent !important;
  color: #ffffff !important;
}
.cc-banner--football .cc-text {
  color: rgba(230, 237, 243, 0.72) !important;
}
.cc-banner--football .cc-btn-muted {
  background-color: transparent !important;
  color: #e6edf3 !important;
  border: 1px solid rgba(255, 255, 255, 0.22) !important;
}
.cc-banner--football .cc-btn-muted:hover {
  background-color: rgba(255, 255, 255, 0.06) !important;
  border-color: rgba(255, 255, 255, 0.36) !important;
}
.cc-banner--football .cc-btn-primary {
  background-color: #1f6feb !important;
  color: #ffffff !important;
  border: 0 !important;
}
.cc-banner--football .cc-btn-primary:hover {
  background-color: #388bfd !important;
}

/* Preferences modal */
.cc-modal-backdrop.cc-modal-backdrop--football {
  background-color: rgba(0, 0, 0, 0.72) !important;
}
.cc-modal-backdrop--football .cc-modal {
  background-color: #0d1117 !important;
  color: #e6edf3 !important;
  border: 1px solid rgba(255, 255, 255, 0.10) !important;
}
.cc-modal-backdrop--football .cc-modal-header {
  border-bottom-color: rgba(255, 255, 255, 0.10) !important;
}
.cc-modal-backdrop--football .cc-modal-title {
  color: #ffffff !important;
}
.cc-modal-backdrop--football .cc-icon-btn {
  color: rgba(230, 237, 243, 0.65) !important;
  background-color: transparent !important;
}
.cc-modal-backdrop--football .cc-icon-btn:hover {
  color: #ffffff !important;
}
.cc-modal-backdrop--football .cc-modal-desc {
  color: rgba(230, 237, 243, 0.72) !important;
}
.cc-modal-backdrop--football .cc-pref {
  border-color: rgba(255, 255, 255, 0.08) !important;
  background-color: rgba(255, 255, 255, 0.03) !important;
}
.cc-modal-backdrop--football .cc-pref-name {
  color: #ffffff !important;
}
.cc-modal-backdrop--football .cc-pref-desc {
  color: rgba(230, 237, 243, 0.65) !important;
}
.cc-modal-backdrop--football .cc-modal-footer {
  border-top-color: rgba(255, 255, 255, 0.10) !important;
  background-color: transparent !important;
}
.cc-modal-backdrop--football .cc-btn-muted {
  background-color: transparent !important;
  color: #e6edf3 !important;
  border: 1px solid rgba(255, 255, 255, 0.22) !important;
}
.cc-modal-backdrop--football .cc-btn-muted:hover {
  background-color: rgba(255, 255, 255, 0.06) !important;
  border-color: rgba(255, 255, 255, 0.36) !important;
}
.cc-modal-backdrop--football .cc-btn-primary {
  background-color: #1f6feb !important;
  color: #ffffff !important;
  border: 0 !important;
}
.cc-modal-backdrop--football .cc-btn-primary:hover {
  background-color: #388bfd !important;
}
/* Switch toggles in preferences */
.cc-modal-backdrop--football .cc-switch {
  background-color: rgba(255, 255, 255, 0.12) !important;
}
.cc-modal-backdrop--football .cc-switch--on {
  background-color: #1f6feb !important;
}
.cc-modal-backdrop--football .cc-switch-thumb {
  background-color: #ffffff !important;
}
.cc-modal-backdrop--football .cc-always-on {
  color: rgba(230, 237, 243, 0.55) !important;
}

</style>
